import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header, Footer } from '../../components';
import storage from '../../utils/storage';
import './Auth.css';

const Auth = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // 如果用户已登录，重定向到首页
    const currentUser = storage.getCurrentUser();
    if (currentUser) {
      navigate('/');
    }
  }, [navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // 清除错误
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!isLogin) {
      // 注册验证
      if (!formData.username.trim()) {
        newErrors.username = '用户名不能为空';
      } else if (formData.username.length < 3) {
        newErrors.username = '用户名至少需要3个字符';
      } else if (formData.username.length > 20) {
        newErrors.username = '用户名不能超过20个字符';
      }

      if (!formData.email.trim()) {
        newErrors.email = '邮箱不能为空';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = '请输入有效的邮箱地址';
      }

      if (!formData.password) {
        newErrors.password = '密码不能为空';
      } else if (formData.password.length < 6) {
        newErrors.password = '密码至少需要6个字符';
      }

      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = '两次输入的密码不一致';
      }
    } else {
      // 登录验证
      if (!formData.username.trim() && !formData.email.trim()) {
        newErrors.username = '用户名或邮箱不能为空';
      }

      if (!formData.password) {
        newErrors.password = '密码不能为空';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const users = storage.getUsers();

      if (isLogin) {
        // 登录逻辑
        const user = users.find(u => 
          (u.username === formData.username || u.email === formData.username) &&
          u.password === formData.password
        );

        if (user) {
          // 登录成功
          storage.setCurrentUser(user);
          // 触发自定义事件
          window.dispatchEvent(new Event('userChange'));
          navigate('/');
        } else {
          setErrors({
            general: '用户名或密码错误'
          });
        }
      } else {
        // 注册逻辑
        // 检查用户名是否已存在
        const existingUser = users.find(u => 
          u.username === formData.username || u.email === formData.email
        );

        if (existingUser) {
          setErrors({
            general: existingUser.username === formData.username 
              ? '该用户名已被使用' 
              : '该邮箱已被注册'
          });
          setLoading(false);
          return;
        }

        // 创建新用户
        const newUser = {
          id: `user_${Date.now()}`,
          username: formData.username.trim(),
          email: formData.email.trim(),
          password: formData.password, // 实际项目中应该加密
          avatar: `https://api.dicebear.com/7.x/avataaars/svg?seed=${formData.username}`,
          bio: '这个人很懒，什么都没有留下...',
          createdAt: new Date().toISOString()
        };

        // 保存用户
        users.push(newUser);
        storage.saveUsers(users);
        storage.setCurrentUser(newUser);
        
        // 触发自定义事件
        window.dispatchEvent(new Event('userChange'));
        
        alert('注册成功！欢迎加入我们的博客社区！');
        navigate('/');
      }
    } catch (error) {
      console.error('Auth error:', error);
      setErrors({
        general: '操作失败，请稍后重试'
      });
    } finally {
      setLoading(false);
    }
  };

  const toggleMode = () => {
    setIsLogin(!isLogin);
    setErrors({});
    setFormData({
      username: '',
      email: '',
      password: '',
      confirmPassword: ''
    });
  };

  return (
    <div className="app">
      <Header />
      <main className="main">
        <div className="container-sm">
          <div className="auth-container">
            <div className="auth-card">
              <div className="auth-header">
                <h1>{isLogin ? '欢迎回来' : '加入我们'}</h1>
                <p>
                  {isLogin 
                    ? '登录您的账户继续写作之旅' 
                    : '创建账户开始您的博客之旅'
                  }
                </p>
              </div>

              <form onSubmit={handleSubmit} className="auth-form">
                {errors.general && (
                  <div className="error-message">
                    {errors.general}
                  </div>
                )}

                <div className="form-group">
                  <label htmlFor="username">
                    {isLogin ? '用户名或邮箱' : '用户名'}
                  </label>
                  <input
                    type="text"
                    id="username"
                    name="username"
                    value={formData.username}
                    onChange={handleInputChange}
                    placeholder={isLogin ? '请输入用户名或邮箱' : '请输入用户名'}
                  />
                  {errors.username && (
                    <span className="error-text">{errors.username}</span>
                  )}
                </div>

                {!isLogin && (
                  <div className="form-group">
                    <label htmlFor="email">邮箱</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="请输入邮箱地址"
                    />
                    {errors.email && (
                      <span className="error-text">{errors.email}</span>
                    )}
                  </div>
                )}

                <div className="form-group">
                  <label htmlFor="password">密码</label>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder="请输入密码"
                  />
                  {errors.password && (
                    <span className="error-text">{errors.password}</span>
                  )}
                </div>

                {!isLogin && (
                  <div className="form-group">
                    <label htmlFor="confirmPassword">确认密码</label>
                    <input
                      type="password"
                      id="confirmPassword"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      placeholder="请再次输入密码"
                    />
                    {errors.confirmPassword && (
                      <span className="error-text">{errors.confirmPassword}</span>
                    )}
                  </div>
                )}

                <button 
                  type="submit" 
                  className="submit-btn"
                  disabled={loading}
                >
                  {loading ? '处理中...' : (isLogin ? '登录' : '注册')}
                </button>
              </form>

              <div className="auth-footer">
                <p>
                  {isLogin ? '还没有账户？' : '已有账户？'}
                  <button 
                    type="button" 
                    onClick={toggleMode}
                    className="toggle-btn"
                  >
                    {isLogin ? '立即注册' : '立即登录'}
                  </button>
                </p>
              </div>
            </div>

            {/* 功能介绍 */}
            <div className="auth-features">
              <h3>博客功能</h3>
              <ul>
                <li>✍️ 支持Markdown编辑</li>
                <li>📱 响应式设计</li>
                <li>🔒 本地数据存储</li>
                <li>🎨 现代化界面</li>
                <li>📊 文章统计分析</li>
                <li>🏷️ 标签分类管理</li>
              </ul>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Auth;